"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PermissionService = void 0;
const router_1 = require("@keplr-wallet/router");
const types_1 = require("./types");
const cosmos_1 = require("@keplr-wallet/cosmos");
class PermissionService {
    constructor(kvStore, privilegedOrigins) {
        this.kvStore = kvStore;
        this.globalPermissionMap = {};
        this.permissionMap = {};
        this.privilegedOrigins = new Map();
        this.onChainRemoved = (chainId) => {
            this.removeAllPermissions(chainId);
        };
        for (const origin of privilegedOrigins) {
            this.privilegedOrigins.set(origin, true);
        }
        this.restore();
    }
    init(interactionService, chainsService, keyRingService) {
        this.interactionService = interactionService;
        this.chainsService = chainsService;
        this.keyRingService = keyRingService;
        this.chainsService.addChainRemovedHandler(this.onChainRemoved);
    }
    checkOrGrantBasicAccessPermission(env, chainIds, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            // Try to unlock the key ring before checking or granting the basic permission.
            yield this.keyRingService.enable(env);
            if (typeof chainIds === "string") {
                chainIds = [chainIds];
            }
            const ungrantedChainIds = [];
            for (const chainId of chainIds) {
                if (!this.hasPermisson(chainId, types_1.getBasicAccessPermissionType(), origin)) {
                    ungrantedChainIds.push(chainId);
                }
            }
            if (ungrantedChainIds.length > 0) {
                yield this.grantBasicAccessPermission(env, ungrantedChainIds, [origin]);
            }
            yield this.checkBasicAccessPermission(env, chainIds, origin);
        });
    }
    disable(env, chainIds, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            // Delete permissions granted to origin.
            // If chain ids are specified, only the permissions granted to each chain id are deleted (In this case, permissions such as getChainInfosWithoutEndpoints() are not deleted).
            // Else, remove all permissions granted to origin (In this case, permissions that are not assigned to each chain, such as getChainInfosWithoutEndpoints(), are also deleted).
            yield this.keyRingService.enable(env);
            if (typeof chainIds === "string") {
                chainIds = [chainIds];
            }
            if (chainIds.length > 0) {
                for (const chainId of chainIds) {
                    yield this.removeAllTypePermissionToChainId(chainId, [origin]);
                }
            }
            else {
                yield this.removeAllTypePermission([origin]);
                yield this.removeAllTypeGlobalPermission([origin]);
            }
        });
    }
    checkOrGrantPermission(env, url, chainIds, type, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: Merge with `checkOrGrantBasicAccessPermission` method
            // Try to unlock the key ring before checking or granting the basic permission.
            yield this.keyRingService.enable(env);
            const ungrantedChainIds = [];
            for (const chainId of chainIds) {
                if (!this.hasPermisson(chainId, type, origin)) {
                    ungrantedChainIds.push(chainId);
                }
            }
            if (ungrantedChainIds.length > 0) {
                yield this.grantPermission(env, url, ungrantedChainIds, type, [origin]);
            }
            for (const chainId of chainIds) {
                this.checkPermission(env, chainId, type, origin);
            }
        });
    }
    checkOrGrantGlobalPermission(env, url, type, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            // Try to unlock the key ring before checking or granting the basic permission.
            yield this.keyRingService.enable(env);
            if (!this.hasGlobalPermission(type, origin)) {
                yield this.grantGlobalPermission(env, url, type, [origin]);
            }
            this.checkGlobalPermission(env, type, origin);
        });
    }
    grantPermission(env, url, chainIds, type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            if (env.isInternalMsg) {
                return;
            }
            const permissionData = {
                chainIds,
                type,
                origins,
            };
            yield this.interactionService.waitApprove(env, url, types_1.INTERACTION_TYPE_PERMISSION, permissionData);
            yield this.addPermission(chainIds, type, origins);
        });
    }
    grantBasicAccessPermission(env, chainIds, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const chainId of chainIds) {
                // Make sure that the chain info is registered.
                yield this.chainsService.getChainInfo(chainId);
            }
            yield this.grantPermission(env, "/access", chainIds, types_1.getBasicAccessPermissionType(), origins);
        });
    }
    grantGlobalPermission(env, url, type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            if (env.isInternalMsg) {
                return;
            }
            const permissionData = {
                type,
                origins,
            };
            yield this.interactionService.waitApprove(env, url, types_1.INTERACTION_TYPE_GLOBAL_PERMISSION, permissionData);
            yield this.addGlobalPermission(type, origins);
        });
    }
    checkPermission(env, chainId, type, origin) {
        if (env.isInternalMsg) {
            return;
        }
        if (!this.hasPermisson(chainId, type, origin)) {
            throw new router_1.KeplrError("permission", 130, `${origin} is not permitted`);
        }
    }
    checkBasicAccessPermission(env, chainIds, origin) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const chainId of chainIds) {
                // Make sure that the chain info is registered.
                yield this.chainsService.getChainInfo(chainId);
                this.checkPermission(env, chainId, types_1.getBasicAccessPermissionType(), origin);
            }
        });
    }
    checkGlobalPermission(env, type, origin) {
        if (env.isInternalMsg) {
            return;
        }
        if (!this.hasGlobalPermission(type, origin)) {
            throw new router_1.KeplrError("permission", 130, `${origin} is not permitted`);
        }
    }
    hasPermisson(chainId, type, origin) {
        // Privileged origin can pass the any permission.
        if (this.privilegedOrigins.get(origin)) {
            return true;
        }
        const permissionsInChain = this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier];
        if (!permissionsInChain) {
            return false;
        }
        const innerMap = permissionsInChain[type];
        return !(!innerMap || !innerMap[origin]);
    }
    hasGlobalPermission(type, origin) {
        // Privileged origin can pass the any permission.
        if (this.privilegedOrigins.get(origin)) {
            return true;
        }
        const originMap = this.globalPermissionMap[type];
        if (!originMap) {
            return false;
        }
        return !!originMap[origin];
    }
    getPermissionOrigins(chainId, type) {
        const origins = [];
        const permissionsInChain = this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier];
        if (!permissionsInChain) {
            return [];
        }
        const innerMap = permissionsInChain[type];
        if (!innerMap) {
            return [];
        }
        for (const origin of Object.keys(innerMap)) {
            if (innerMap[origin]) {
                origins.push(origin);
            }
        }
        return origins;
    }
    getGlobalPermissionOrigins(type) {
        const originMap = Object.assign({}, this.globalPermissionMap[type]);
        return Object.keys(originMap).filter((origin) => {
            return originMap[origin];
        });
    }
    getOriginPermittedChains(origin, type) {
        var _a;
        const chains = [];
        for (const chain of Object.keys(this.permissionMap)) {
            const permissionInChain = this.permissionMap[chain];
            const originMap = (_a = (permissionInChain ? permissionInChain[type] : undefined)) !== null && _a !== void 0 ? _a : {};
            for (const _origin of Object.keys(originMap)) {
                if (_origin === origin && originMap[_origin]) {
                    chains.push(chain);
                }
            }
        }
        return chains;
    }
    addPermission(chainIds, type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const chainId of chainIds) {
                let permissionsInChain = this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier];
                if (!permissionsInChain) {
                    permissionsInChain = {};
                    this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier] = permissionsInChain;
                }
                let innerMap = permissionsInChain[type];
                if (!innerMap) {
                    innerMap = {};
                    permissionsInChain[type] = innerMap;
                }
                for (const origin of origins) {
                    innerMap[origin] = true;
                }
            }
            yield this.save();
        });
    }
    addGlobalPermission(type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            const originMap = Object.assign({}, this.globalPermissionMap[type]);
            for (const origin of origins) {
                originMap[origin] = true;
            }
            this.globalPermissionMap[type] = originMap;
            yield this.save();
        });
    }
    removePermission(chainId, type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            const permissionsInChain = this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier];
            if (!permissionsInChain) {
                return;
            }
            const innerMap = permissionsInChain[type];
            if (!innerMap) {
                return;
            }
            for (const origin of origins) {
                delete innerMap[origin];
            }
            yield this.save();
        });
    }
    removeAllTypePermission(origins) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const identifier of Object.keys(this.permissionMap)) {
                const permissionsInChain = this.permissionMap[identifier];
                if (!permissionsInChain) {
                    return;
                }
                for (const type of Object.keys(permissionsInChain)) {
                    const innerMap = permissionsInChain[type];
                    if (!innerMap) {
                        return;
                    }
                    for (const origin of origins) {
                        delete innerMap[origin];
                    }
                }
            }
            yield this.save();
        });
    }
    removeAllTypePermissionToChainId(chainId, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            const permissionsInChain = this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier];
            if (!permissionsInChain) {
                return;
            }
            for (const type of Object.keys(permissionsInChain)) {
                const innerMap = permissionsInChain[type];
                if (!innerMap) {
                    return;
                }
                for (const origin of origins) {
                    delete innerMap[origin];
                }
            }
            yield this.save();
        });
    }
    removeGlobalPermission(type, origins) {
        return __awaiter(this, void 0, void 0, function* () {
            const originMap = Object.assign({}, this.globalPermissionMap[type]);
            for (const origin of origins) {
                delete originMap[origin];
            }
            this.globalPermissionMap[type] = originMap;
            yield this.save();
        });
    }
    removeAllTypeGlobalPermission(origins) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const type of Object.keys(this.globalPermissionMap)) {
                const originMap = Object.assign({}, this.globalPermissionMap[type]);
                for (const origin of origins) {
                    delete originMap[origin];
                }
                this.globalPermissionMap[type] = originMap;
            }
            yield this.save();
        });
    }
    removeAllPermissions(chainId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.permissionMap[cosmos_1.ChainIdHelper.parse(chainId).identifier] = undefined;
            yield this.save();
        });
    }
    restore() {
        return __awaiter(this, void 0, void 0, function* () {
            const map = yield this.kvStore.get("permissionMap");
            if (map) {
                this.permissionMap = map;
            }
            const globalMap = yield this.kvStore.get("globalPermissionMap");
            if (globalMap) {
                this.globalPermissionMap = globalMap;
            }
        });
    }
    save() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.kvStore.set("permissionMap", this.permissionMap);
            yield this.kvStore.set("globalPermissionMap", this.globalPermissionMap);
        });
    }
}
exports.PermissionService = PermissionService;
//# sourceMappingURL=service.js.map